import React, { useState, SyntheticEvent, useEffect } from 'react';
import { Form, FormSpy, FormProps, FormRenderProps } from 'react-final-form';
import { ValidationResult, Dictionary } from '@unitedtraders/validators';
import { Context } from './context';

import { FinalFormField } from './FinalFormField';

import { FormContainerRenderProps } from './types';
import { FormApi } from 'final-form';

interface Props<T extends Dictionary> extends FormProps<T> {
  validateTips?: (values: T) => ValidationResult;
  children?: (props: FormContainerRenderProps<T>) => JSX.Element;
  onSubmit: (values: T, formApi: FormApi<T>) => void;
  i18NamespaceForServerErrors?: string[] | string;
}

function FormContainer<T>(props: Props<T>) {
  if (props.component) {
    throw new Error(`Can't render with component prop. Use children or render property.`);
  }

  const [submittedAtLeastOneTime, setSubmitted] = useState(false);
  const [tipsStore, setTips] = useState({});

  const init = (args: FormRenderProps<T>) => {
    useEffect(() => {
      if (props.validateTips) {
        setTips(props.validateTips(args.values));
      }
    }, [args.values]);

    return {
      ...args,
      submittedAtLeastOneTime,
      submitIsDisabled: submittedAtLeastOneTime ? args.invalid : false,
      tips: tipsStore,
      handleSubmit: (event?: SyntheticEvent<HTMLFormElement, Event>) => {
        setSubmitted(true);
        return args.handleSubmit(event);
      }
    };
  };

  return (
    <Context.Provider
      value={{
        tips: tipsStore,
        submittedAtLeastOneTime,
        i18NamespaceForServerErrors: props.i18NamespaceForServerErrors
      }}
    >
      <Form
        {...props}
        render={args => {
          return (
            <FormSpy
              subscription={{ active: true }}
              render={props.render && (() => props.render?.(init(args)))}
            >
              {() => props.children?.(init(args))}
            </FormSpy>
          );
        }}
      />
    </Context.Provider>
  );
}

export { FormContainer, FinalFormField };
